import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { changePassword } from '../services/studentAuthApi';
import { useAuthStore } from '../stores/authStore';

// BP 26 — /account/password. Lives OUTSIDE AppShell (its own RequireAuth
// wrapper in routes.tsx) so it needs no /auth/me hydration: a student whose
// account is gated by must_change_password must be able to reach this page
// with nothing more than a valid token. Reached two ways: the login flow
// navigating here directly when must_change_password is true (?forced=1), or
// apiClient's 403 password_change_required interceptor bouncing here from
// anywhere else in the app.
function messageForError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    if (data?.error === 'invalid_credentials') return 'Your current password is incorrect.';
    if (data?.error === 'weak_password') return data.message || 'That password is not strong enough.';
    if (data?.error === 'invalid_body') return 'Please fill in both fields.';
  }
  return 'Could not change your password. Please try again.';
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const forced = params.get('forced') === '1';
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const inputClass =
    'w-full rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink placeholder:text-ws-ink-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 10) {
      setError('New password must be at least 10 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setBusy(true);
    try {
      const { user: updated } = await changePassword(currentPassword, newPassword);
      setUser(updated);
      setDone(true);
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ws-bg px-4 py-8">
      <Card className="w-full max-w-sm p-8">
        <h1 className="text-xl font-semibold text-ws-ink">
          {forced ? 'Choose a new password' : 'Change your password'}
        </h1>
        <p className="mt-1.5 text-sm text-ws-ink-3">
          {forced
            ? 'You are signing in with a temporary password. Choose one only you know before continuing.'
            : user?.given_name
              ? `Update the password for ${user.given_name}'s account.`
              : 'Update your account password.'}
        </p>

        {done ? (
          <div role="status" className="mt-6 rounded-md border border-ws-success bg-ws-success-quiet px-3 py-2 text-sm text-ws-success">
            Password updated — taking you to the dashboard…
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
            {error && (
              <div role="alert" aria-invalid="true" className="rounded-md border border-ws-danger bg-ws-surface-2 px-3 py-2 text-sm text-ws-danger">
                {error}
              </div>
            )}

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ws-ink-2">
                {forced ? 'Temporary password' : 'Current password'}
              </span>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={inputClass}
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ws-ink-2">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 10 characters"
                minLength={10}
                className={inputClass}
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ws-ink-2">Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
                required
              />
            </label>

            <Button type="submit" size="lg" loading={busy} className="w-full mt-1">
              Change password
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
